import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DollarSign, Wallet, History, User, Gamepad2, Loader2 } from 'lucide-react';

interface GameData {
  id: string;
  title: string;
  provider: string;
  image: string;
  rating: number;
  category: string;
  description: string;
  game_url?: string;
}

// Mock data for user dashboard (mantido para elementos não relacionados a jogos)
const userBalance = 1250.75;
const userBonus = 150.00;
const recentTransactions = [
  { id: 1, type: 'Depósito', amount: 500.00, date: '2025-09-28', status: 'Concluído' },
  { id: 2, type: 'Aposta', amount: -25.00, date: '2025-09-29', status: 'Concluído' },
  { id: 3, type: 'Ganho', amount: 75.50, date: '2025-09-29', status: 'Concluído' },
  { id: 4, type: 'Saque', amount: -300.00, date: '2025-09-30', status: 'Pendente' },
];

const Dashboard: React.FC = () => {
  const [recentGames, setRecentGames] = useState<GameData[]>([]);
  const [recommendedGames, setRecommendedGames] = useState<GameData[]>([]);
  const [loadingGames, setLoadingGames] = useState<boolean>(true);
  const [errorGames, setErrorGames] = useState<string | null>(null);

  useEffect(() => {
    const fetchGamesForDashboard = async () => {
      setLoadingGames(true);
      setErrorGames(null);
      try {
        const response = await fetch("https://slots.virtualebank.com.br/games");
        if (!response.ok) {
          throw new Error("Failed to fetch games for dashboard");
        }
        const allGames: GameData[] = await response.json();

        // Simular jogos recentes (por exemplo, os últimos 2 jogos)
        setRecentGames(allGames.slice(0, 2));

        // Simular jogos recomendados (por exemplo, os próximos 2 jogos após os recentes)
        setRecommendedGames(allGames.slice(2, 4));

      } catch (err) {
        setErrorGames((err as Error).message);
      } finally {
        setLoadingGames(false);
      }
    };

    fetchGamesForDashboard();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto py-8 md:py-12">
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-8">
          Bem-vindo(a), Usuário!
        </h1>

        {/* Account Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card className="bg-gradient-to-br from-primary/20 to-background border-primary/50">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Saldo Disponível
              </CardTitle>
              <Wallet className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground">R$ {userBalance.toFixed(2)}</div>
              <p className="text-xs text-muted-foreground mt-1">
                + R$ {userBonus.toFixed(2)} em bônus
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Depósito Rápido
              </CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <Button className="w-full bg-gradient-primary hover:shadow-glow">Depositar</Button>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Minha Conta
              </CardTitle>
              <User className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <Button variant="outline" className="w-full" asChild>
                <Link to="/profile">Ver Perfil</Link>
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center">
            <History className="w-6 h-6 mr-2 text-primary" /> Atividade Recente
          </h2>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border">
                  <thead className="bg-secondary/30"><tr><th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Tipo</th><th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Valor</th><th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Data</th><th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th></tr></thead>
                  <tbody className="divide-y divide-border">
                    {recentTransactions.map((transaction) => (
                      <tr key={transaction.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">{transaction.type}</td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${transaction.amount > 0 ? 'text-green-500' : 'text-red-500'}`}>
                          R$ {transaction.amount.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{transaction.date}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            transaction.status === 'Concluído' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {transaction.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Games */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center">
            <Gamepad2 className="w-6 h-6 mr-2 text-primary" /> Jogos Recentes
          </h2>
          {loadingGames ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <p className="ml-3 text-lg text-muted-foreground">Carregando jogos recentes...</p>
            </div>
          ) : errorGames ? (
            <p className="text-center text-red-500">Erro ao carregar jogos recentes: {errorGames}</p>
          ) : (recentGames.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {recentGames.map((game) => (
                <Link to={`/game/${game.id}`} key={game.id}>
                  <Card className="group relative overflow-hidden border-border hover:border-primary/50 transition-all duration-300 hover:shadow-card cursor-pointer h-full">
                    <CardContent className="p-0">
                      <div className="relative h-32 overflow-hidden">
                        <img
                          src={game.image}
                          alt={game.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors">
                          {game.title}
                        </h3>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground">Nenhum jogo recente encontrado.</p>
          ))}
        </div>

        {/* Recommended Games */}
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center">
            <Gamepad2 className="w-6 h-6 mr-2 text-primary" /> Jogos Recomendados
          </h2>
          {loadingGames ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <p className="ml-3 text-lg text-muted-foreground">Carregando jogos recomendados...</p>
            </div>
          ) : errorGames ? (
            <p className="text-center text-red-500">Erro ao carregar jogos recomendados: {errorGames}</p>
          ) : (recommendedGames.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {recommendedGames.map((game) => (
                <Link to={`/game/${game.id}`} key={game.id}>
                  <Card className="group relative overflow-hidden border-border hover:border-primary/50 transition-all duration-300 hover:shadow-card cursor-pointer h-full">
                    <CardContent className="p-0">
                      <div className="relative h-32 overflow-hidden">
                        <img
                          src={game.image}
                          alt={game.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors">
                          {game.title}
                        </h3>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground">Nenhum jogo recomendado encontrado.</p>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
